import { useMemo, useState } from 'react';
import { useAppData } from '../context/AppDataContext.jsx';
import { phaseForDateClient, PHASE_LABEL } from '../phases.js';

const MONTHS_IT = [
  'Gennaio',
  'Febbraio',
  'Marzo',
  'Aprile',
  'Maggio',
  'Giugno',
  'Luglio',
  'Agosto',
  'Settembre',
  'Ottobre',
  'Novembre',
  'Dicembre',
];
const DOW_IT = ['L', 'M', 'M', 'G', 'V', 'S', 'D'];

function isoDate(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function daysInMonth(y, m) {
  return new Date(y, m + 1, 0).getDate();
}

// 0=lunedì .. 6=domenica
function startOffset(y, m) {
  const dow = new Date(y, m, 1).getDay(); // 0=dom..6=sab
  return (dow + 6) % 7;
}

export default function CyclePage() {
  const {
    periods,
    episodes,
    cycleStats,
    addPeriod,
    updatePeriod,
    deletePeriod,
  } = useAppData();
  const [view, setView] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  const [form, setForm] = useState({ startDate: '', endDate: '', note: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const headacheDates = useMemo(
    () => new Set(episodes.map((e) => e.date)),
    [episodes]
  );

  const today = new Date().toISOString().slice(0, 10);

  function prevMonth() {
    setView(({ year, month }) =>
      month === 0 ? { year: year - 1, month: 11 } : { year, month: month - 1 }
    );
  }
  function nextMonth() {
    setView(({ year, month }) =>
      month === 11 ? { year: year + 1, month: 0 } : { year, month: month + 1 }
    );
  }

  async function onSubmit(e) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await addPeriod({
        startDate: form.startDate,
        endDate: form.endDate || null,
        note: form.note,
      });
      setForm({ startDate: '', endDate: '', note: '' });
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setBusy(false);
    }
  }

  const offset = startOffset(view.year, view.month);
  const total = daysInMonth(view.year, view.month);
  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= total; d++) cells.push(d);

  return (
    <div>
      <div className="card">
        <div className="cal-nav">
          <button className="secondary" onClick={prevMonth}>&larr;</button>
          <strong>
            {MONTHS_IT[view.month]} {view.year}
          </strong>
          <button className="secondary" onClick={nextMonth}>&rarr;</button>
        </div>
        <div className="calendar">
          {DOW_IT.map((d, i) => (
            <div key={`dow-${i}`} className="dow">{d}</div>
          ))}
          {cells.map((d, i) => {
            if (d == null) return <div key={`empty-${i}`} className="day empty" />;
            const dateStr = isoDate(view.year, view.month, d);
            const { phase } = phaseForDateClient(periods, dateStr);
            const cls = ['day'];
            if (phase && phase !== 'unknown') cls.push(phase);
            if (dateStr === today) cls.push('today');
            if (headacheDates.has(dateStr)) cls.push('has-headache');
            const title = `${dateStr} · ${PHASE_LABEL[phase] || ''}`;
            return (
              <div key={dateStr} className={cls.join(' ')} title={title}>
                {d}
              </div>
            );
          })}
        </div>
        <div className="row" style={{ marginTop: '0.75rem', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span className="phase phase-menstruation">Mestruazioni</span>
          <span className="phase phase-follicolare">Follicolare</span>
          <span className="phase phase-fertile">Fertile</span>
          <span className="phase phase-ovulazione">Ovulazione</span>
          <span className="phase phase-luteale">Luteale</span>
          <span className="phase phase-premestruale">Premestruale</span>
          <span>• mal di testa</span>
        </div>
        {cycleStats?.nextPeriodEstimate && (
          <div className="muted" style={{ marginTop: '0.5rem' }}>
            Lunghezza media ciclo: {cycleStats.cycleLength}g · prossimo previsto: {cycleStats.nextPeriodEstimate}{' · '}
            ovulazione: {cycleStats.nextOvulationEstimate}{' · '}
            fertile: {cycleStats.fertileWindowEstimate?.from} – {cycleStats.fertileWindowEstimate?.to}
          </div>
        )}
      </div>

      <div className="card">
        <h2>Aggiungi un periodo</h2>
        {err && <div className="error">{err}</div>}
        <form onSubmit={onSubmit}>
          <div className="field">
            <label>Data inizio *</label>
            <input
              type="date"
              required
              value={form.startDate}
              onChange={(e) => setForm({ ...form, startDate: e.target.value })}
            />
          </div>
          <div className="field">
            <label>Data fine (opzionale)</label>
            <input
              type="date"
              value={form.endDate}
              onChange={(e) => setForm({ ...form, endDate: e.target.value })}
            />
          </div>
          <div className="field">
            <label>Note</label>
            <input
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
            />
          </div>
          <button type="submit" disabled={busy}>
            {busy ? 'Salvataggio…' : 'Salva periodo'}
          </button>
        </form>
      </div>

      <div className="card">
        <h2>Storico periodi</h2>
        {periods.length === 0 ? (
          <p className="muted">Nessun periodo registrato.</p>
        ) : (
          <ul className="list">
            {periods.map((p) => (
              <li key={p.id}>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <div>
                    <strong>{p.startDate}</strong>
                    {p.endDate && <> &rarr; {p.endDate}</>}
                    {p.note && <div className="muted">{p.note}</div>}
                  </div>
                  <div className="row">
                    {!p.endDate && (
                      <button
                        className="secondary"
                        onClick={() =>
                          updatePeriod(p.id, {
                            endDate: new Date().toISOString().slice(0, 10),
                          })
                        }
                      >
                        Termina oggi
                      </button>
                    )}
                    <button
                      className="danger"
                      onClick={() => {
                        if (window.confirm('Eliminare questo periodo?')) deletePeriod(p.id);
                      }}
                    >
                      Elimina
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
